import { useState, useEffect } from 'react';
import { hasPermission } from '../../../lib/permissions';
import apiClient from '../../../lib/api';
import { resolveImageUrl } from '../../../lib/utils';

interface AboutSettings {
  id?: string;
  hero_title: string;
  hero_subtitle: string;
  hero_image_url?: string;
  story_title: string;
  story_content: string;
  story_image_url?: string;
  mission_title: string;
  mission_content: string;
  vision_title: string;
  vision_content: string;
  stats_clients: number;
  stats_experience: number;
  stats_locations: number;
  stats_services: number;
  whatsapp_number: string;
  created_at?: string;
  updated_at?: string;
}

interface ImageUploadState {
  hero: File | null;
  story: File | null;
}

interface TeamMember {
  id: string;
  name: string;
  position: string;
  experience: string;
  image_url: string;
  bio?: string;
  specialties?: string;
  order_index?: number;
  is_active: number;
}

const getImageUrl = (image: string | File | null | undefined, type: 'hero' | 'story' = 'hero') => {
  if (!image) {
    return '';
  }
  if (typeof image === 'string') {
    return resolveImageUrl(image);
  }
  return URL.createObjectURL(image);
};

const AboutManager = () => {
  const [settings, setSettings] = useState<AboutSettings>({
    hero_title: '',
    hero_subtitle: '',
    hero_image_url: '',
    story_title: '',
    story_content: '',
    story_image_url: '',
    mission_title: '',
    mission_content: '',
    vision_title: '',
    vision_content: '',
    stats_clients: 5000,
    stats_experience: 10,
    stats_locations: 3,
    stats_services: 27,
    whatsapp_number: '917484984048'
  });
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingTeamMember, setEditingTeamMember] = useState<TeamMember | null>(null);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [teamFile, setTeamFile] = useState<File | null>(null);
  const [imageFiles, setImageFiles] = useState<ImageUploadState>({ hero: null, story: null });

  const [uploadStatus, setUploadStatus] = useState<string>('');

  const handleFileChange = async (type: 'hero' | 'story', e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setUploadStatus('Error: File too large (Max 5MB)');
      return;
    }

    try {
      setLoading(true);
      setUploadStatus(`Uploading ${type} image...`);

      const formData = new FormData();
      formData.append('image', file);

      // Use standard apiClient
      console.log(`Uploading ${type} image...`);
      const uploadResult = await apiClient.uploadImage(formData, 'about');
      console.log('Upload result:', uploadResult);

      setSettings(prev => ({
        ...prev,
        [`${type}_image_url`]: uploadResult.imageUrl
      }));

      setUploadStatus(`✅ Success! ${type} image uploaded. Click Save to persist.`);

    } catch (error: any) {
      console.error(`Failed to upload ${type} image:`, error);
      setUploadStatus(`❌ Upload Failed: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [aboutSettings, team] = await Promise.all([
        apiClient.getAboutSettings(),
        apiClient.getTeamMembers()
      ]);

      // Ensure all required fields are present with defaults
      const defaultSettings: AboutSettings = {
        hero_title: 'About Our Story',
        hero_subtitle: "Discover the journey behind India's premier men's grooming destination",
        hero_image_url: '',
        story_title: 'Crafting Excellence Since 2014',
        story_content: 'Fully A/C Salon began with a simple vision: to provide men with the highest quality grooming services in a luxurious, comfortable environment.',
        story_image_url: '',
        mission_title: 'Our Mission',
        mission_content: "To provide exceptional men's grooming services in a luxurious, fully air-conditioned environment.",
        vision_title: 'Our Vision',
        vision_content: "To become India's most trusted and recognized premium men's grooming brand.",
        stats_clients: 5000,
        stats_experience: 10,
        stats_locations: 3,
        stats_services: 27,
        whatsapp_number: '917484984048'
      };

      setSettings({ ...defaultSettings, ...aboutSettings });
      setTeamMembers(team);
    } catch (error) {
      console.error('Failed to load about data:', error);
      // Set default settings on error
      setSettings({
        hero_title: 'About Our Story',
        hero_subtitle: "Discover the journey behind India's premier men's grooming destination",
        hero_image_url: '',
        story_title: 'Crafting Excellence Since 2014',
        story_content: 'Fully A/C Salon began with a simple vision: to provide men with the highest quality grooming services in a luxurious, comfortable environment.',
        story_image_url: '',
        mission_title: 'Our Mission',
        mission_content: "To provide exceptional men's grooming services in a luxurious, fully air-conditioned environment.",
        vision_title: 'Our Vision',
        vision_content: "To become India's most trusted and recognized premium men's grooming brand.",
        stats_clients: 5000,
        stats_experience: 10,
        stats_locations: 3,
        stats_services: 27,
        whatsapp_number: '917484984048'
      });
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);

      // Handle file uploads (DEPRECATED - now handled on selection)
      // Images are already uploaded and URLs are in settings

      // Save all settings
      console.log('Saving settings:', settings);
      await apiClient.updateAboutSettings(settings);

      // Show success
      alert('Settings saved successfully!');
      await loadData();

    } catch (error: any) {
      console.error('Failed to save settings:', error);
      alert(`Failed to save settings: ${error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddTeamMember = () => {
    const newMember: TeamMember = {
      id: '',
      name: '',
      position: '',
      experience: '',
      image_url: '',
      bio: '',
      specialties: '',
      order_index: teamMembers.length + 1,
      is_active: 1
    };
    setEditingTeamMember(newMember);
    setShowTeamModal(true);
  };

  const handleEditTeamMember = (member: TeamMember) => {
    setEditingTeamMember({ ...member });
    setShowTeamModal(true);
  };

  const handleSaveTeamMember = async () => {
    if (!editingTeamMember) return;

    try {
      if (teamFile) {
        const form = new FormData();
        form.append('name', editingTeamMember.name);
        form.append('position', editingTeamMember.position);
        form.append('experience', editingTeamMember.experience);
        form.append('bio', editingTeamMember.bio || '');
        form.append('specialties', editingTeamMember.specialties || '');
        form.append('order_index', String(editingTeamMember.order_index || 0));
        form.append('team', teamFile);
        if (editingTeamMember.id) {
          await apiClient.updateTeamMember(editingTeamMember.id, form);
        } else {
          await apiClient.createTeamMember(form);
        }
      } else {
        if (editingTeamMember.id) {
          await apiClient.updateTeamMember(editingTeamMember.id, editingTeamMember);
        } else {
          await apiClient.createTeamMember(editingTeamMember);
        }
      }

      await loadData();
      setShowTeamModal(false);
      setEditingTeamMember(null);
      setTeamFile(null);
    } catch (error: any) {
      console.error('Failed to save team member:', error);
      alert(`Failed to save team member: ${error.message}`);
    }
  };

  const handleDeleteTeamMember = async (id: string) => {
    console.log('[AboutManager] handleDeleteTeamMember called with id:', id);
    const ok = window.confirm('Are you sure you want to delete this team member?');
    console.log('[AboutManager] confirm returned:', ok);
    if (!ok) return;

    try {
      console.log('[AboutManager] Proceeding with API delete for team member:', id);
      await apiClient.deleteTeamMember(id);
      console.log('[AboutManager] Delete successful, reloading data');
      await loadData();
    } catch (error) {
      console.error('[AboutManager] Failed to delete team member:', error);
      alert('Failed to delete team member. Please try again.');
    }
  };



  const hasPermFor = (permId: string) => {
    return hasPermission(permId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400"></div>
        <span className="ml-2">Loading about page settings...</span>
      </div>
    );
  }

  const sections = [
    { id: 'hero', name: 'About Main Image', icon: 'ri-image-line', permKey: 'about-hero' },
    { id: 'story', name: 'Our Story', icon: 'ri-book-line', permKey: 'about-story' },
    { id: 'mission', name: 'Mission & Vision', icon: 'ri-target-line', permKey: 'about-mission' },
    { id: 'stats', name: 'Statistics', icon: 'ri-bar-chart-line', permKey: 'about-stats' },
    { id: 'team', name: 'Team Members', icon: 'ri-team-line', permKey: 'about-team' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-900">About Page Management</h3>
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="bg-primary-400 hover:bg-primary-500 text-black px-6 py-3 rounded-lg font-semibold transition-all duration-300 disabled:opacity-50 flex items-center"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <i className="ri-save-line mr-2"></i>
              Save Settings
            </>
          )}
        </button>
      </div>

      {/* Section Navigation */}
      <div className="flex flex-wrap gap-2">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center ${activeSection === section.id
              ? 'bg-primary-400 text-black'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
          >
            <i className={`${section.icon} mr-2`}></i>
            {section.name}
          </button>
        ))}
      </div>

      {/* Hero Section */}
      {activeSection === 'hero' && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h4 className="text-lg font-bold text-gray-900 mb-4">About Main Image</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Hero Title</label>
              <input
                type="text"
                value={settings.hero_title}
                onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                placeholder="About Our Story"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Hero Subtitle</label>
              <textarea
                value={settings.hero_subtitle}
                onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                rows={3}
                placeholder="Discover the journey behind India's premier men's grooming destination"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Background Image</label>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Upload from Computer</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange('hero', e)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Max size: 5MB</p>
                  {uploadStatus && (
                    <div className={`mt-2 p-2 rounded text-sm font-bold ${uploadStatus.includes('Success') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {uploadStatus}
                    </div>
                  )}
                </div>

                <div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Preview (Auto-updates on upload):</p>
                    <img
                      src={getImageUrl(settings.hero_image_url, 'hero')}
                      alt="Hero Preview"
                      className="w-full h-40 object-cover rounded-lg border bg-gray-50"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/400x200?text=Image+Not+Found';
                      }}
                    />
                    {settings.hero_image_url && (
                      <p className="text-xs text-gray-500 mt-1">URL: {settings.hero_image_url}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Story Section */}
      {activeSection === 'story' && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h4 className="text-lg font-bold text-gray-900 mb-4">Our Story Section</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Story Title</label>
              <input
                type="text"
                value={settings.story_title}
                onChange={(e) => setSettings({ ...settings, story_title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                placeholder="Crafting Excellence Since 2014"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Story Content</label>
              <textarea
                value={settings.story_content}
                onChange={(e) => setSettings({ ...settings, story_content: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                rows={5}
                placeholder="Tell your brand story..."
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Story Image</label>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Upload from Computer</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange('story', e)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Max size: 5MB</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Or Enter Image URL</label>
                  <input
                    type="url"
                    value={imageFiles.story ? '' : (settings.story_image_url || '')}
                    onChange={(e) => {
                      setSettings({ ...settings, story_image_url: e.target.value });
                      setImageFiles(prev => ({ ...prev, story: null }));
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    placeholder="https://example.com/story.jpg"
                    disabled={!!imageFiles.story}
                  />
                  {imageFiles.story && (
                    <p className="text-xs text-blue-600 mt-1">File selected - URL input disabled</p>
                  )}
                </div>
                <div>
                  <div className="mt-2 mb-2">
                    <p className="text-xs text-gray-500">Selected File: {imageFiles.story ? imageFiles.story.name : 'None'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Preview:</p>
                    <img
                      src={getImageUrl(imageFiles.story || settings.story_image_url, 'story')}
                      alt="Story Preview"
                      className="w-full h-40 object-cover rounded-lg border bg-gray-50"
                      onError={(e) => {
                        console.error('Story image failed to load:', settings.story_image_url);
                        e.currentTarget.src = 'https://via.placeholder.com/400x200?text=Image+Not+Found';
                      }}
                    />
                    {!imageFiles.story && settings.story_image_url && (
                      <p className="text-xs text-gray-500 mt-1">URL: {settings.story_image_url}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mission & Vision Section */}
      {activeSection === 'mission' && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h4 className="text-lg font-bold text-gray-900 mb-4">Mission & Vision</h4>
          <div className="space-y-6">
            <div>
              <h5 className="text-md font-semibold text-gray-800 mb-3">Mission</h5>
              <div className="space-y-3">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Mission Title</label>
                  <input
                    type="text"
                    value={settings.mission_title}
                    onChange={(e) => setSettings({ ...settings, mission_title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    placeholder="Our Mission"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Mission Content</label>
                  <textarea
                    value={settings.mission_content}
                    onChange={(e) => setSettings({ ...settings, mission_content: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    rows={4}
                    placeholder="Describe your mission..."
                  />
                </div>
              </div>
            </div>

            <div>
              <h5 className="text-md font-semibold text-gray-800 mb-3">Vision</h5>
              <div className="space-y-3">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Vision Title</label>
                  <input
                    type="text"
                    value={settings.vision_title}
                    onChange={(e) => setSettings({ ...settings, vision_title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    placeholder="Our Vision"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Vision Content</label>
                  <textarea
                    value={settings.vision_content}
                    onChange={(e) => setSettings({ ...settings, vision_content: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    rows={4}
                    placeholder="Describe your vision..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Section */}
      {activeSection === 'stats' && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h4 className="text-lg font-bold text-gray-900 mb-4">Statistics</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Happy Clients</label>
              <input
                type="number"
                value={settings.stats_clients}
                onChange={(e) => setSettings({ ...settings, stats_clients: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                min="0"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Years Experience</label>
              <input
                type="number"
                value={settings.stats_experience}
                onChange={(e) => setSettings({ ...settings, stats_experience: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                min="0"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Locations</label>
              <input
                type="number"
                value={settings.stats_locations}
                onChange={(e) => setSettings({ ...settings, stats_locations: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                min="0"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Premium Services</label>
              <input
                type="number"
                value={settings.stats_services}
                onChange={(e) => setSettings({ ...settings, stats_services: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                min="0"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-700 font-semibold mb-2">WhatsApp Number</label>
              <input
                type="text"
                value={settings.whatsapp_number}
                onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                placeholder="917484984048"
              />
            </div>
          </div>
        </div>
      )}

      {/* Team Section */}
      {activeSection === 'team' && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-bold text-gray-900">Team Members</h4>
            <button
              onClick={handleAddTeamMember}
              className="bg-primary-400 hover:bg-primary-500 text-black px-4 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center"
            >
              <i className="ri-add-line mr-2"></i>
              Add Team Member
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teamMembers.map((member) => (
              <div key={member.id} className="border rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <img
                    src={resolveImageUrl(member.image_url) || 'https://via.placeholder.com/60'}
                    alt={member.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <h5 className="font-semibold text-gray-900">{member.name}</h5>
                    <p className="text-sm text-gray-600">{member.position}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-3">{member.experience}</p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditTeamMember(member)}
                    className="flex-1 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteTeamMember(member.id)}
                    className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {teamMembers.length === 0 && (
            <p className="text-center text-gray-500 py-8">No team members added yet.</p>
          )}
        </div>
      )}

      {/* Team Member Modal */}
      {showTeamModal && editingTeamMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {editingTeamMember.id ? 'Edit Team Member' : 'Add Team Member'}
              </h3>
              <button
                onClick={() => setShowTeamModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Name</label>
                <input
                  type="text"
                  value={editingTeamMember.name}
                  onChange={(e) => setEditingTeamMember({ ...editingTeamMember, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Position</label>
                <input
                  type="text"
                  value={editingTeamMember.position}
                  onChange={(e) => setEditingTeamMember({ ...editingTeamMember, position: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Experience</label>
                <input
                  type="text"
                  value={editingTeamMember.experience}
                  onChange={(e) => setEditingTeamMember({ ...editingTeamMember, experience: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="e.g., 10+ Years"
                />
              </div>



              <div>
                <label className="block text-gray-700 font-semibold mb-2">Or Upload Photo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    if (f && f.size > 5 * 1024 * 1024) {
                      alert('File too large. Max 5MB allowed.');
                      return;
                    }
                    setTeamFile(f);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
                <p className="text-sm text-gray-600 mt-1">Max size 5MB</p>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Bio (Optional)</label>
                <textarea
                  value={editingTeamMember.bio || ''}
                  onChange={(e) => setEditingTeamMember({ ...editingTeamMember, bio: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Specialties (Optional)</label>
                <input
                  type="text"
                  value={editingTeamMember.specialties || ''}
                  onChange={(e) => setEditingTeamMember({ ...editingTeamMember, specialties: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="e.g., Hair Coloring, Beard Styling"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowTeamModal(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTeamMember}
                className="px-6 py-2 bg-primary-400 hover:bg-primary-500 text-black rounded-lg font-semibold transition-all duration-300"
              >
                Save Member
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutManager;
